import type { Editor } from '@tiptap/core';
import { useEditorState } from '@tiptap/react';
import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../web/components-v2/Button';
import {
	ALIGNMENTS,
	DEFAULT_TEXT_COLOR,
	EMAIL_TEXT_COLORS,
	HIGHLIGHT_COLOR,
	keepEditorSelection,
	runComposerFormatCommand,
	TEXT_SIZES,
} from './composerFormattingActions';
import {
	AlignIcon,
	BulletListIcon,
	ChevronDownIcon,
	ImageIcon,
	LinkIcon,
	OrderedListIcon,
	PaperclipIcon,
	RedoIcon,
	UndoIcon,
} from './composerIcons';
import { composerStyles as styles } from './composerStyles';

interface ComposerFormattingBarProps {
	editor: Editor | null;
	onOpenLink: () => void;
	onInsertImage: () => void;
	onAttachClick: () => void;
}

/**
 * Persistent formatting controls: undo/redo, text size, bold / italic / underline, text color,
 * highlight, link, lists and alignment. Selection-scoped
 * formatting is also offered by the bubble menu; both drive the same editor commands.
 */
export const ComposerFormattingBar: React.FC<ComposerFormattingBarProps> = ({
	editor,
	onOpenLink,
	onInsertImage,
	onAttachClick,
}) => {
	const [openPopover, setOpenPopover] = React.useState<'size' | 'color' | null>(null);
	const barRef = React.useRef<HTMLDivElement>(null);

	const barState = useEditorState({
		editor,
		selector: ({ editor: current }) =>
			current
				? {
						bold: current.isActive('bold'),
						italic: current.isActive('italic'),
						underline: current.isActive('underline'),
						bulletList: current.isActive('bulletList'),
						orderedList: current.isActive('orderedList'),
						align: ALIGNMENTS.find(value => current.isActive({ textAlign: value })) ?? 'left',
						highlight: current.isActive('highlight'),
						color: (current.getAttributes('textStyle').color as string | undefined) ?? DEFAULT_TEXT_COLOR,
						canUndo: current.can().undo(),
						canRedo: current.can().redo(),
					}
				: null,
	});

	React.useEffect(() => {
		if (!openPopover) return;
		const handleMouseDown = (event: MouseEvent) => {
			if (barRef.current && !barRef.current.contains(event.target as Node)) setOpenPopover(null);
		};
		document.addEventListener('mousedown', handleMouseDown);
		return () => document.removeEventListener('mousedown', handleMouseDown);
	}, [openPopover]);

	if (!editor || !barState) return null;

	const cycleAlignment = () => {
		const nextIndex = (ALIGNMENTS.indexOf(barState.align) + 1) % ALIGNMENTS.length;
		editor.chain().focus().setTextAlign(ALIGNMENTS[nextIndex]).run();
	};

	const applyFontSize = (fontSize: string | null) => {
		const chain = editor.chain().focus();
		if (fontSize) chain.setFontSize(fontSize).run();
		else chain.unsetFontSize().run();
		setOpenPopover(null);
	};

	const applyColor = (hex: string) => {
		editor.chain().focus().setColor(hex).run();
		setOpenPopover(null);
	};

	return (
		<div ref={barRef} className={css(styles.formattingBar)}>
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.barBtn]}
				onMouseDown={keepEditorSelection}
				onClick={() => editor.chain().focus().undo().run()}
				disabled={!barState.canUndo}
				title='Undo'
				aria-label='Undo'
			>
				<UndoIcon />
			</Button>
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.barBtn]}
				onMouseDown={keepEditorSelection}
				onClick={() => editor.chain().focus().redo().run()}
				disabled={!barState.canRedo}
				title='Redo'
				aria-label='Redo'
			>
				<RedoIcon />
			</Button>
			<div className={css(styles.barDivider)} />
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.barBtnWide]}
				onMouseDown={keepEditorSelection}
				onClick={() => setOpenPopover(openPopover === 'size' ? null : 'size')}
				title='Text size'
				aria-label='Text size'
			>
				Aa
				<ChevronDownIcon />
			</Button>
			{openPopover === 'size' && (
				<div className={css(styles.sizePopover)}>
					{TEXT_SIZES.map(size => (
						<Button
							key={size.label}
							variant='ghost'
							size='sm'
							styles={[styles.sizeOption]}
							onMouseDown={keepEditorSelection}
							onClick={() => applyFontSize(size.fontSize)}
							style={{ fontSize: size.previewPx }}
						>
							{size.label}
						</Button>
					))}
				</div>
			)}
			<div className={css(styles.barDivider)} />
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.barBtn, styles.barBtnBold, ...(barState.bold ? [styles.barBtnActive] : [])]}
				onMouseDown={keepEditorSelection}
				onClick={() => runComposerFormatCommand(editor, 'bold')}
				title='Bold'
				aria-label='Bold'
			>
				B
			</Button>
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.barBtn, styles.barBtnItalic, ...(barState.italic ? [styles.barBtnActive] : [])]}
				onMouseDown={keepEditorSelection}
				onClick={() => runComposerFormatCommand(editor, 'italic')}
				title='Italic'
				aria-label='Italic'
			>
				I
			</Button>
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.barBtn, styles.barBtnUnderline, ...(barState.underline ? [styles.barBtnActive] : [])]}
				onMouseDown={keepEditorSelection}
				onClick={() => runComposerFormatCommand(editor, 'underline')}
				title='Underline'
				aria-label='Underline'
			>
				U
			</Button>
			<div className={css(styles.barDivider)} />
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.barBtn]}
				onMouseDown={keepEditorSelection}
				onClick={() => setOpenPopover(openPopover === 'color' ? null : 'color')}
				title='Text color'
				aria-label='Text color'
			>
				<span className={css(styles.barGlyphStack)}>
					<span className={css(styles.barGlyphLetter)}>A</span>
					<span className={css(styles.barGlyphSwatch)} style={{ background: barState.color }} />
				</span>
			</Button>
			{openPopover === 'color' && (
				<div className={css(styles.colorPopover)}>
					{EMAIL_TEXT_COLORS.map(hex => (
						<Button
							key={hex}
							variant='ghost'
							size='sm'
							styles={[styles.colorSwatch]}
							onMouseDown={keepEditorSelection}
							onClick={() => applyColor(hex)}
							style={{ background: hex }}
							title={hex}
							aria-label={`Text color ${hex}`}
						/>
					))}
				</div>
			)}
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.barBtn, ...(barState.highlight ? [styles.barBtnActive] : [])]}
				onMouseDown={keepEditorSelection}
				onClick={() => runComposerFormatCommand(editor, 'highlight')}
				title='Highlight'
				aria-label='Highlight'
			>
				<span className={css(styles.barGlyphStack)}>
					<span className={css(styles.barGlyphLetter)}>A</span>
					<span className={css(styles.barGlyphSwatch)} style={{ background: HIGHLIGHT_COLOR }} />
				</span>
			</Button>
			<div className={css(styles.barDivider)} />
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.barBtn]}
				onMouseDown={keepEditorSelection}
				onClick={onOpenLink}
				title='Insert link'
				aria-label='Insert link'
			>
				<LinkIcon />
			</Button>
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.barBtn]}
				onMouseDown={keepEditorSelection}
				onClick={onInsertImage}
				title='Insert image'
				aria-label='Insert image'
			>
				<ImageIcon />
			</Button>
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.barBtn]}
				onMouseDown={keepEditorSelection}
				onClick={onAttachClick}
				title='Attach files'
				aria-label='Attach files'
			>
				<PaperclipIcon />
			</Button>
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.barBtn, ...(barState.bulletList ? [styles.barBtnActive] : [])]}
				onMouseDown={keepEditorSelection}
				onClick={() => runComposerFormatCommand(editor, 'bulletList')}
				title='Bulleted list'
				aria-label='Bulleted list'
			>
				<BulletListIcon />
			</Button>
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.barBtn, ...(barState.orderedList ? [styles.barBtnActive] : [])]}
				onMouseDown={keepEditorSelection}
				onClick={() => editor.chain().focus().toggleOrderedList().run()}
				title='Numbered list'
				aria-label='Numbered list'
			>
				<OrderedListIcon />
			</Button>
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.barBtn]}
				onMouseDown={keepEditorSelection}
				onClick={cycleAlignment}
				title={`Align — now ${barState.align}`}
				aria-label={`Align — now ${barState.align}`}
			>
				<AlignIcon />
			</Button>
		</div>
	);
};
